import { filter, some } from "lodash"

/**
 * Media kinds the file drop targets understand. AUDIO is declared so the drop
 * plumbing has a slot for it, but stays out of ENABLED_MEDIA_DROP_KINDS until
 * an audio upload pipeline exists (there is nowhere to store or play it yet).
 */
export const MEDIA_DROP_KIND = {
  IMAGE: "IMAGE",
  AUDIO: "AUDIO",
} as const

export type MediaDropKind =
  (typeof MEDIA_DROP_KIND)[keyof typeof MEDIA_DROP_KIND]

export const ENABLED_MEDIA_DROP_KINDS: ReadonlySet<MediaDropKind> = new Set([
  MEDIA_DROP_KIND.IMAGE,
])

/** HEIC/HEIF drags can arrive with an empty MIME type; match by extension. */
const HEIC_EXTENSION = /\.(heic|heif)$/i

export const getMediaDropKind = (
  mimeType: string,
  filename = ""
): MediaDropKind | null => {
  if (mimeType.startsWith("image/") || HEIC_EXTENSION.test(filename)) {
    return MEDIA_DROP_KIND.IMAGE
  }
  if (mimeType.startsWith("audio/")) return MEDIA_DROP_KIND.AUDIO

  return null
}

const isDroppableMedia = (mimeType: string, filename = ""): boolean => {
  const kind = getMediaDropKind(mimeType, filename)

  return kind != null && ENABLED_MEDIA_DROP_KINDS.has(kind)
}

/**
 * Dragenter/dragover gate: mid-drag only MIME types are readable (no
 * filenames), so an untyped file (HEIC on some platforms) is admitted
 * optimistically here and re-checked by getDroppedMediaFiles on drop.
 */
export const dragHasDroppableMedia = (
  dataTransfer: Pick<DataTransfer, "items"> | null
): boolean => {
  if (!dataTransfer) return false

  return some(
    Array.from(dataTransfer.items ?? []),
    (item) =>
      item.kind === "file" && (item.type === "" || isDroppableMedia(item.type))
  )
}

/** Drop-time filter: keeps only files the uploader can handle today. */
export const getDroppedMediaFiles = (
  dataTransfer: Pick<DataTransfer, "files"> | null
): File[] => {
  if (!dataTransfer) return []

  return filter(Array.from(dataTransfer.files ?? []), (file) =>
    isDroppableMedia(file.type, file.name)
  )
}
